import { StyleSheet, Platform } from 'react-native';
import { AdminColors } from '../../Admin/constants/adminColors';
import FontFamily from '../../../constants/fonts';

const isWeb = Platform.OS === 'web';

const styles = StyleSheet.create({
  // ── Root — flex column, fills the full screen ─────────────────────────
  // Matches HTML .people-flow-screen { position:fixed; inset: var(--nav-header-height) 0 0 0;
  // flex-direction:column; display:flex }. Using position:fixed (web only) instead of relying
  // on flex:1 cascading all the way up through App.tsx's layout — any ancestor View there
  // without an explicit minHeight:0 lets content grow and pushes the footer off-screen,
  // forcing the whole page to scroll instead of just this screen's body.
  root: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
    ...(isWeb
      ? ({
          position: 'fixed',
          top: 64,
          // Leaves room for DLOSidebar (collapsed width 72) so the overlay doesn't cover it.
          left: 72,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          overflow: 'hidden',
        } as any)
      : {}),
  },

  // ── Scrollable body ───────────────────────────────────────────────────
  // Matches HTML .people-flow-screen-body { flex: auto; min-height:0; overflow: auto; }
  scrollBody: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    // Matches HTML padding: 22px clamp(16px,3vw,44px) 28px
    paddingHorizontal: isWeb ? 44 : 12,
    paddingTop: isWeb ? 22 : 16,
    paddingBottom: 28,
    // Matches HTML .people-flow-page { gap: 16px }
    gap: 16,
  },

  // ── Page head — scrolls with content ─────────────────────────────────
  // Matches HTML .people-flow-pagehead { justify-content:space-between; align-items:flex-start; gap:16px; display:flex }
  pagehead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  pageheadText: {
    flex: 1,
    gap: 4,
  },
  // Matches HTML .people-flow-pagehead h1 { font: nav-heading-h4-bold = 700 28px/40px }
  // Mobile: nav-heading-h6-bold = 700 20px/28px
  pageTitle: {
    fontFamily: FontFamily.bold,
    fontSize: isWeb ? 28 : 20,
    lineHeight: isWeb ? 40 : 28,
    color: AdminColors.textPrimary,
    marginBottom: 4,
  },
  // Matches HTML .people-flow-pagehead p { font: nav-small-regular = 400 14px/20px }
  pageSubtitle: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: AdminColors.textSecondary,
  },

  // ── Resume draft info card ───────────────────────────────────────────
  resumeDraftCard: {
    borderWidth: 1,
    borderColor: AdminColors.blue100,
    borderRadius: 6,
    backgroundColor: AdminColors.blue25,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  resumeDraftText: {
    fontFamily: FontFamily.medium,
    fontSize: 13,
    lineHeight: 18,
    color: AdminColors.blue900,
  },

  // ── Fixed footer ──────────────────────────────────────────────────────
  // Matches HTML .people-flow-screen-foot
  // flex:none so it never shrinks and always sits at the bottom of root
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Matches HTML padding: 12px clamp(16px,3vw,44px)
    paddingHorizontal: isWeb ? 44 : 12,
    paddingVertical: 12,
    minHeight: 64,
    backgroundColor: AdminColors.bgSurface,
    borderTopWidth: 1,
    borderTopColor: AdminColors.borderDefault,
    gap: 12,
    flexShrink: 0,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  footerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },

  // ── Footer buttons ────────────────────────────────────────────────────
  // Matches HTML .btn { height:40px; border-radius:24px; padding:8px 24px; font:nav-body-bold=700 16px }
  // Base "ghost" style — transparent border, no background (used by Exit in observation via Leave modal)
  footerButton: {
    height: 40,
    borderWidth: 1,
    borderColor: 'transparent',
    borderRadius: 24,
    paddingHorizontal: 24,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    lineHeight: 22,
    color: AdminColors.textSecondary,
  },
  // Secondary = Exit, Save Draft, Back buttons
  // Matches HTML .btn-secondary { background:surface; border-color:blue-800; color:blue-800 }
  footerButtonSecondary: {
    borderColor: AdminColors.blue800,
    backgroundColor: AdminColors.bgSurface,
  },
  footerButtonSecondaryText: {
    color: AdminColors.blue800,
  },
  // Primary = Complete button (enabled)
  // Matches HTML .btn-primary { background:blue-800; color:white }
  footerButtonPrimary: {
    borderColor: AdminColors.blue800,
    backgroundColor: AdminColors.blue800,
  },
  footerButtonPrimaryText: {
    color: AdminColors.textOnDark,
  },
  // Primary disabled — matches HTML .people-flow-screen-foot .btn-primary:disabled
  // { background: blue-200; border-color: blue-200; color: blue-900; opacity:1 }
  footerButtonPrimaryDisabled: {
    borderColor: AdminColors.blue200,
    backgroundColor: AdminColors.blue200,
  },
  footerButtonPrimaryDisabledText: {
    color: AdminColors.blue900,
  },

  // ── Validation error bar ──────────────────────────────────────────────
  // Matches HTML .people-flow-validation — orange left-border strip
  validationBar: {
    marginTop: 4,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#D97706',
    backgroundColor: '#FEF9EE',
    borderRadius: 2,
  },
  validationText: {
    fontFamily: FontFamily.regular,
    fontSize: isWeb ? 13 : 12,
    color: '#92400E',
    lineHeight: 18,
  },

  // ── Leave Activity modal ──────────────────────────────────────────────
  // Full-screen semi-transparent overlay
  leaveModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  // White card — widened so the three footer buttons fit on one row
  leaveModalCard: {
    width: '100%',
    maxWidth: 640,
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 14,
    padding: 28,
    gap: 14,
  },
  leaveModalTitle: {
    fontFamily: FontFamily.bold,
    fontSize: isWeb ? 18 : 17,
    color: AdminColors.textPrimary,
  },
  leaveModalBody: {
    fontFamily: FontFamily.regular,
    fontSize: isWeb ? 14 : 13,
    color: AdminColors.textSecondary,
    lineHeight: 20,
  },
  leaveModalFooter: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    gap: 8,
    marginTop: 4,
  },
  // Equal-width — stretches each button to fill the footer row
  leaveModalButton: {
    // flex: 1,
    paddingHorizontal: 14,
  },
});

export default styles;
